import Fuse from "fuse.js";
import { Recipe } from "../models/recipe";
import { Ingredient } from "../models/ingredient";

export const SEARCH_MIN_QUERY_LENGTH = 3;

export interface RecipeSearchResult {
  results: Recipe[];
  // recipeId -> the ingredient name(s) that matched the query (for highlighting).
  matchedIngredients: Record<string, string[]>;
}

// Fuzzy-search recipes by title AND ingredient names (ingredients live in their
// own slice, so we join each recipe with its ingredient names first). Title is
// weighted higher. Returns the matched recipes plus which ingredient(s) matched
// each, so the UI can show why a recipe appeared. Shared by both apps.
export function searchRecipes(
  recipes: Recipe[],
  ingredients: Ingredient[],
  query: string,
): RecipeSearchResult {
  const q = query.trim();
  if (q.length < SEARCH_MIN_QUERY_LENGTH) {
    return { results: [], matchedIngredients: {} };
  }

  const namesByRecipe = new Map<string, string[]>();
  for (const ing of ingredients) {
    if (!ing.name || ing.group) continue;
    const list = namesByRecipe.get(ing.recipeId) ?? [];
    list.push(ing.name);
    // Substitutes are searchable too — "margarine" should find the recipe.
    if (ing.substitute?.name) list.push(ing.substitute.name);
    namesByRecipe.set(ing.recipeId, list);
  }

  const searchable = recipes.map((recipe) => ({
    ...recipe,
    ingredientNames: namesByRecipe.get(recipe.id) ?? [],
  }));

  const fuse = new Fuse(searchable, {
    keys: [
      { name: "title", weight: 2 },
      { name: "ingredientNames", weight: 1 },
      { name: "tags", weight: 1 },
    ],
    threshold: 0.3, // ~70% similarity
    includeMatches: true, // so we can surface which ingredient matched
  });

  const raw = fuse.search(q);

  // Rank in two tiers: recipes whose TITLE matched the query come first, then
  // recipes matched only on an ingredient. Within each tier keep Fuse's own score
  // order (best first) — a stable sort with an index tiebreak preserves it.
  const ranked = raw
    .map((r, index) => {
      const matches = r.matches ?? [];
      return {
        item: r.item as Recipe,
        index,
        titleMatch: matches.some((m) => m.key === "title"),
        ingredientNames: matches
          .filter((m) => m.key === "ingredientNames" && m.value)
          .map((m) => m.value as string),
      };
    })
    .sort((a, b) =>
      a.titleMatch !== b.titleMatch ? (a.titleMatch ? -1 : 1) : a.index - b.index,
    );

  const matchedIngredients: Record<string, string[]> = {};
  for (const r of ranked) {
    if (r.ingredientNames.length) {
      matchedIngredients[r.item.id] = Array.from(new Set(r.ingredientNames));
    }
  }

  return { results: ranked.map((r) => r.item), matchedIngredients };
}
